/**
 * BUTTON TOKENS
 *
 * Custom additions are marked with "Custom" comments below.
 */
import { nbxVar as v } from '../../../tokens/generated/nbx-tokens.js';
import { SEMANTIC, type SemanticTokens } from '../semantic.js';

export function createButtonTokens(s: SemanticTokens) {
  return {
    /** Base spacing for all button variants */
    base: {
      paddingX: v('button.base.padding-x'), // --button-base-padding-x: var(--spacing-16)
      paddingY: v('button.base.padding-y'), // --button-base-padding-y: var(--spacing-16)
      gap: v('button.base.gap'), // --button-base-gap: var(--spacing-8)
      radius: v('button.base.radius'), // --button-base-radius: var(--radius-8)
      transitionDuration: s.transition.normal, // --button-base-transition-duration
      labelFontWeight: s.typography.fontWeight.bold, // --button-base-label-font-weight
      labelLineHeight: s.typography.lineHeight.xl, // --button-base-label-line-height (1.5rem)
      focusRingColor: s.focus.ring, // --button-base-focus-ring-color: #c14f79 (primary)
      focusRingWidth: '2px', // --button-base-focus-ring-width
      focusRingStyle: 'solid', // --button-base-focus-ring-style
      focusRingOffset: '2px', // --button-base-focus-ring-offset
    },
    /** Small button sizing — the DS compact variant, 48px tall */
    sm: {
      fontSize: s.typography.fontSize.base, // --button-sm-font-size
      paddingX: s.layout.gap.md, // --button-sm-padding-x: var(--spacing-16)
      paddingY: `calc(${s.layout.gap.sm} + 3px)`, // --button-sm-padding-y: 11px, so 11 + 24 + 11 + 2px of border is 48
    },
    /** Large button sizing */
    lg: {
      fontSize: s.typography.fontSize.xl, // --button-lg-font-size
      padding: s.layout.padding.md, // --button-lg-padding
    },
    /** Primary button (main CTA) */
    primary: {
      bg: v('button.primary.bg'), // --button-primary-bg: var(--color-rentals-800)
      bgHover: v('button.primary.bg-hover'), // --button-primary-bg-hover: var(--color-rentals-900)
      bgPressed: v('button.primary.bg-pressed'), // --button-primary-bg-pressed: var(--color-rentals-950)
      bgDisabled: v('button.primary.bg-disabled'), // --button-primary-bg-disabled: var(--surface-disabled)
      fg: v('button.primary.fg'), // --button-primary-fg: var(--text-on-color)
      fgDisabled: v('button.primary.fg-disabled'), // --button-primary-fg-disabled: var(--text-on-color)
      border: s.border.primary, // --button-primary-border: var(--border-primary)
    },
    /** Secondary button (neutral actions) */
    secondary: {
      bg: v('button.secondary.bg'), // --button-secondary-bg: var(--color-rentsody-800)
      bgHover: v('button.secondary.bg-hover'), // --button-secondary-bg-hover: var(--color-rentsody-700)
      bgPressed: v('button.secondary.bg-pressed'), // --button-secondary-bg-pressed: var(--color-rentsody-950)
      bgDisabled: v('button.secondary.bg-disabled'), // --button-secondary-bg-disabled: var(--surface-disabled)
      fg: v('button.secondary.fg'), // --button-secondary-fg: var(--text-on-color)
      fgDisabled: v('button.secondary.fg-disabled'), // --button-secondary-fg-disabled: var(--text-on-color)
      border: s.border.secondary, // --button-secondary-border: var(--border-secondary)
    },
    /** Ghost button (minimal/outline) */
    ghost: {
      bg: v('button.ghost.bg'), // --button-info-bg: var(--surface-ground)
      bgHover: v('button.ghost.bg-hover'), // --button-info-bg-hover: var(--color-rentsody-50)
      bgPressed: v('button.ghost.bg-pressed'), // --button-info-bg-pressed: var(--color-rentsody-200)
      bgDisabled: v('button.ghost.bg-disabled'), // --button-info-bg-disabled: var(--surface-disabled)
      fg: v('button.ghost.fg'), // --button-info-fg: var(--text-secondary)
      fgDisabled: v('button.ghost.fg-disabled'), // --button-info-fg-disabled: var(--text-on-color)
      border: v('button.ghost.border'), // --button-info-border: var(--border-secondary)
      /** Ghost button specific spacing */
      paddingX: s.layout.gap.md, // --button-info-button-padding-x: var(--spacing-16)
      paddingY: s.layout.gap.md, // --button-info-button-padding-y: var(--spacing-16)
      gap: s.layout.gap.sm, // --button-info-button-gap: var(--spacing-8)
      fontWeight: s.typography.fontWeight.bold, // --button-ghost-font-weight
      fontSize: s.typography.fontSize.base, // --button-ghost-font-size
    },
    /** Action button (icon + label, contextual actions) */
    action: {
      bg: v('button.action.bg'), // --button-action-bg: var(--surface-ground)
      bgHover: v('button.action.bg-hover'), // --button-action-bg-hover: var(--color-rentals-50)
      bgPressed: v('button.action.bg-pressed'), // --button-action-bg-pressed: var(--color-rentals-100)
      bgDisabled: v('button.action.bg-disabled'), // --button-action-bg-disabled: var(--surface-disabled)
      fg: v('button.action.fg'), // --button-action-fg: var(--color-rentsody-800)
      fgHover: v('button.action.fg-hover'), // --button-action-fg-hover: var(--text-primary)
      fgDisabled: v('button.action.fg-disabled'), // --button-action-fg-disabled: var(--text-on-color)
      border: s.border.secondary, // --button-action-border: var(--border-secondary)
      size: s.controlSize.md, // --button-action-size (2.75rem)
      iconSize: s.iconSizes.lg, // --button-action-icon-size
      fontWeight: s.typography.fontWeight.bold, // --button-action-font-weight
      fontSize: s.typography.fontSize.base, // --button-action-font-size
      transition: s.transition.fast, // --button-action-transition
    },
    /** Utility button (icon-only, compact) */
    utility: {
      bg: v('button.utility.bg'), // --button-utility-bg: var(--color-rentsody-50)
      bgHover: v('button.utility.bg-hover'), // --button-utility-bg-hover: var(--color-rentsody-100)
      bgPressed: v('button.utility.bg-pressed'), // --button-utility-bg-pressed: var(--color-rentsody-200)
      bgDisabled: v('button.utility.bg-disabled'), // --button-utility-bg-disabled: var(--surface-disabled)
      fg: v('button.utility.fg'), // --button-utility-fg: var(--text-secondary)
      fgDisabled: v('button.utility.fg-disabled'), // --button-utility-fg-disabled: var(--text-on-color)
      border: s.border.secondary, // --button-utility-border: var(--border-secondary)
      size: s.controlSize.md, // --button-utility-size (2.75rem)
      iconSize: s.iconSizes.lg, // --button-utility-icon-size
      transition: s.transition.fast, // --button-utility-transition
    },
    /** Inline button (icon-only, no chrome: the dismiss and overflow triggers inside cards and dialogs) */
    inline: {
      fg: s.text.secondary, // --button-inline-fg: var(--text-secondary)
      fgHover: s.text.primary, // --button-inline-fg-hover: var(--text-primary)
      fgDisabled: s.text.muted, // --button-inline-fg-disabled: var(--text-muted)
      size: s.iconSizes.md, // --button-inline-size (1.25rem)
      iconSize: s.typography.fontSize.sm, // --button-inline-icon-size (0.875rem)
      transition: s.transition.fast, // --button-inline-transition
    },
    /** Status button variants */
    status: {
      success: {
        bg: v('button.status.success.bg'), // --button-status-success-bg: var(--color-green-800)
        bgHover: v('button.status.success.bg-hover'), // --button-status-success-bg-hover: var(--color-green-900)
        bgPressed: v('button.status.success.bg-pressed'), // --button-status-success-bg-pressed: var(--color-green-950)
        bgDisabled: v('button.status.success.bg-disabled'), // --button-status-success-bg-disabled: var(--surface-disabled)
        fg: v('button.status.success.fg'), // --button-status-success-fg: var(--text-on-color)
        fgDisabled: v('button.status.success.fg-disabled'), // --button-status-success-fg-disabled: var(--text-on-color)
        border: s.border.success, // --button-status-success-border: var(--border-success)
      },
      danger: {
        bg: v('button.status.danger.bg'), // --button-status-danger-bg: var(--color-red-800)
        bgHover: v('button.status.danger.bg-hover'), // --button-status-danger-bg-hover: var(--color-red-900)
        bgPressed: v('button.status.danger.bg-pressed'), // --button-status-danger-bg-pressed: var(--color-red-950)
        bgDisabled: v('button.status.danger.bg-disabled'), // --button-status-danger-bg-disabled: var(--surface-disabled)
        fg: v('button.status.danger.fg'), // --button-status-danger-fg: var(--text-on-color)
        fgDisabled: v('button.status.danger.fg-disabled'), // --button-status-danger-fg-disabled: var(--text-on-color)
        border: s.border.danger, // --button-status-danger-border: var(--border-danger)
      },
      warning: {
        bg: v('button.status.warning.bg'), // --button-status-warning-bg: var(--color-yellow-800)
        bgHover: v('button.status.warning.bg-hover'), // --button-status-warning-bg-hover: var(--color-yellow-900)
        bgPressed: v('button.status.warning.bg-pressed'), // --button-status-warning-bg-pressed: var(--color-yellow-950)
        bgDisabled: v('button.status.warning.bg-disabled'), // --button-status-warning-bg-disabled: var(--surface-disabled)
        fg: v('button.status.warning.fg'), // --button-status-warning-fg: var(--text-on-color)
        fgDisabled: v('button.status.warning.fg-disabled'), // --button-status-warning-fg-disabled: var(--text-on-color)
        border: s.border.warning, // --button-status-warning-border: var(--border-warning)
      },
    },

    // ──── Custom tokens ────

    /** Outlined button variant */
    outlined: {
      fg: s.text.primary, // --button-outlined-fg: var(--text-primary)
      border: s.border.default, // --button-outlined-border: var(--border-default)
    },
    /** Text button variant */
    text: {
      fg: s.text.primary, // --button-text-fg: var(--text-primary)
    },
    /** Link button variant */
    link: {
      fg: s.text.link, // --button-link-fg: var(--text-link)
      fgHover: s.text.linkHover, // --button-link-fg-hover: var(--text-link-hover)
      secondaryFg: s.text.secondary, // --button-link-secondary-fg: var(--text-secondary)
      fontSize: s.typography.fontSize.sm, // --button-link-font-size
      lineHeight: s.typography.lineHeight.lg, // --button-link-line-height
      fontWeight: s.typography.fontWeight.bold, // --button-link-font-weight
      fontWeightSem: s.typography.fontWeight.semibold, // --button-link-semibold-font-weight
      gap: s.layout.gap.xs, // --button-link-gap
      iconSize: s.iconSizes.base, // --button-link-icon-size
      transition: s.transition.fast, // --button-link-transition
    },
    /** Sidebar button styles */
    sidebar: {
      border: s.border.primary, // --button-sidebar-border: var(--border-primary)
      gap: s.layout.gap.sm, // --button-sidebar-gap
      padding: s.layout.gap.md, // --button-sidebar-padding
      borderWidth: s.layout.gap.xs, // --button-sidebar-border-width
      iconSize: s.iconSizes.lg, // --button-sidebar-icon-size
      fontWeightNormal: s.typography.fontWeight.normal, // --button-sidebar-font-weight-normal
      fontWeightBold: s.typography.fontWeight.bold, // --button-sidebar-font-weight-bold
      transition: s.transition.fast, // --button-sidebar-transition
    },
  };
}
export const buttonTokens = createButtonTokens(SEMANTIC);
